/**
 * R-75 — centralized confidence band taxonomy + scoring calibration knobs.
 */

export type ConfidenceBand =
  | 'high-confidence'
  | 'moderate-confidence'
  | 'preliminary'
  | 'speculative';

export interface ConfidenceBandDef {
  order: number;
  minScore: number;
  /** Human-readable hedge label used in auto-rewrites. */
  label: string;
}

/** Ordered high → low. `order` drives band-gap severity (block vs warn). */
export const CONFIDENCE_BANDS: Record<ConfidenceBand, ConfidenceBandDef> = {
  'high-confidence': { order: 3, minScore: 0.70, label: 'high confidence' },
  'moderate-confidence': { order: 2, minScore: 0.45, label: 'moderate confidence' },
  preliminary: { order: 1, minScore: 0.20, label: 'preliminary' },
  speculative: { order: 0, minScore: 0, label: 'speculative' },
};

/** Band sequence from strongest to weakest — used for gap math. */
export const CONFIDENCE_BAND_ORDER: ConfidenceBand[] = [
  'high-confidence',
  'moderate-confidence',
  'preliminary',
  'speculative',
];

export const SCORING_WEIGHTS = {
  tier1: 0.45,
  tier1Cap: 0.90,
  tier2: 0.20,
  tier2Cap: 0.40,
  tier3: 0.05,
  tier3Cap: 0.15,
  multiSourceAgreement: 0.10,
  brokenCite: -0.20,
  provenanceGap: -0.15,
} as const;

/** Tier-1 provider names (R-78 precedence list + common aliases). */
export const TIER1_PROVIDER_NAMES = [
  'NREL', 'USGS', 'EIA', 'FEMA', 'USFWS', 'NRC', 'PEERINGDB', 'HIFLD', 'WRI', 'AIRNOW',
  'EPA', 'NOAA', 'Open-Meteo', 'GDELT', 'NFHL', 'IPAC', 'ADAMS', 'ArcGIS',
] as const;

/** Tier-2: curated / regional / secondary government sources. */
export const TIER2_SOURCE_PATTERNS: RegExp[] = [
  /DAM|curated knowledge|knowledge base|regional baseline|government filing|state agency|county assessor/i,
  /industry benchmark|analog site|comparable project/i,
];

/** Declared-band phrase detectors — strongest matching band wins. */
export const CAVEAT_PHRASE_MAP: Record<ConfidenceBand, RegExp[]> = {
  'high-confidence': [
    /\bconfirmed\b/i,
    /\bdefinitively\b/i,
    /\bestablished fact\b/i,
    /\bverified\b/i,
    /\bdemonstrates that\b/i,
    /\bconclusively\b/i,
    /\bwithout (?:a )?doubt\b/i,
  ],
  'moderate-confidence': [
    /\blikely\b/i,
    /\bindicates\b/i,
    /\bsuggests\b/i,
    /\bconsistent with\b/i,
    /\bprobable\b/i,
    /\bexpected to\b/i,
  ],
  preliminary: [
    /\bpreliminary\b/i,
    /\bestimated\b/i,
    /\bappears to\b/i,
    /\bdraft\b/i,
    /\bbaseline\b/i,
    /\bapproximate(?:ly)?\b/i,
    /\bowner-attested\b/i,
  ],
  speculative: [
    /\bmay\b/i,
    /\bmight\b/i,
    /\bcould\b/i,
    /\bpossibly\b/i,
    /\bspeculative\b/i,
    /\buncertain\b/i,
    /\bif confirmed\b/i,
    /\bpending verification\b/i,
  ],
};

/** Map HTML comment shorthand → canonical band id. */
export const CONFIDENCE_COMMENT_ALIASES: Record<string, ConfidenceBand> = {
  high: 'high-confidence',
  'high-confidence': 'high-confidence',
  moderate: 'moderate-confidence',
  'moderate-confidence': 'moderate-confidence',
  medium: 'moderate-confidence',
  preliminary: 'preliminary',
  low: 'preliminary',
  speculative: 'speculative',
  uncertain: 'speculative',
};

export function bandOrder(band: ConfidenceBand): number {
  return CONFIDENCE_BANDS[band].order;
}

export function bandGap(declared: ConfidenceBand, actual: ConfidenceBand): number {
  return CONFIDENCE_BANDS[declared].order - CONFIDENCE_BANDS[actual].order;
}

export function scoreToBand(score: number): ConfidenceBand {
  const clamped = Math.max(0, Math.min(1, score));
  for (const band of CONFIDENCE_BAND_ORDER) {
    if (clamped >= CONFIDENCE_BANDS[band].minScore) return band;
  }
  return 'speculative';
}

export function normalizeCommentBand(raw: string): ConfidenceBand | null {
  const key = raw.trim().toLowerCase().replace(/\s+/g, '-');
  return CONFIDENCE_COMMENT_ALIASES[key] ?? null;
}
